import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ShoppingBag, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get product image
const getProductImage = (product) => {
  if (product?.images?.length > 0) {
    return product.images[0];
  }
  return product?.image_url || '';
};

const CollectionPage = () => {
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCollection();
  }, [slug]);

  const fetchCollection = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/collections/${slug}`);
      setCollection(response.data);
    } catch (error) {
      console.error('Failed to fetch collection', error);
      setError('Collection not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold mb-4">Collection Not Found</h1>
          <p className="text-gray-600 mb-8">This collection may have been removed or doesn't exist.</p>
          <Link to="/products">
            <Button className="bg-black text-white hover:bg-zinc-800">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Browse All Products
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const products = collection.products || [];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />

      {/* Collection Header */}
      <div className="relative">
        {collection.cover_image && (
          <div className="absolute inset-0 h-64 md:h-80">
            <img
              src={collection.cover_image}
              alt={collection.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50" />
          </div>
        )}
        
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 ${collection.cover_image ? 'text-white' : ''}`}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4" data-testid="collection-title">
              {collection.name}
            </h1>
            {collection.description && (
              <p className={`text-lg ${collection.cover_image ? 'text-gray-200' : 'text-gray-600'}`}>
                {collection.description}
              </p>
            )}
            <p className={`mt-4 text-sm ${collection.cover_image ? 'text-gray-300' : 'text-gray-500'}`}>
              {products.length} {products.length === 1 ? 'product' : 'products'}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {products.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No products in this collection yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12" data-testid="collection-products">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group"
                data-testid={`collection-product-${product.id}`}
              >
                <Link to={`/products/${product.id}`}>
                  <div className="relative mb-4 overflow-hidden bg-gray-100 aspect-square">
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {product.original_price && product.original_price > product.price && (
                      <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 text-xs uppercase tracking-widest font-bold">
                        {Math.round((1 - product.price / product.original_price) * 100)}% OFF
                      </div>
                    )}
                    {product.images?.length > 1 && (
                      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                        +{product.images.length - 1} more
                      </div>
                    )}
                  </div>
                  <h3 className="font-heading font-semibold mb-2 group-hover:text-gray-600 transition-colors line-clamp-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">${product.price.toFixed(2)}</span>
                    {product.original_price && product.original_price > product.price && (
                      <span className="text-sm text-gray-400 line-through">
                        ${product.original_price.toFixed(2)}
                      </span>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Back to shop */}
        <div className="mt-16 text-center">
          <Link to="/products">
            <Button variant="outline" className="border-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              View All Products
            </Button>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CollectionPage;
